import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useMediaQuery } from "react-responsive";
import { useAccount } from "wagmi";
import { CountUp } from "countup.js";
import { UserContext } from "../../provider/userprovider";
import { LeaderboardContext } from "../../provider/leaderboard";
import Termresult from "../../components/termresult";
import Pagination from "./Pagination";
import { ReactComponent as SearchIcon } from "../../assets/media/icons/SearchIcon.svg";

import ethLogo from "../../assets/media/icons/ETH-Logo-Medium.png";
import pnlCharacters from "../../assets/media/PNL-Page-Characters.png";
import profileImage from "../../assets/media/icons/NavLogoGold.png";
import sandiskIcon from "../../assets/icons/SanDisk.png";
import pokemonIcon from "../../assets/icons/Pokemon.png";
import goldIcon from "../../assets/icons/Gold.png";
import xIcon from "../../assets/media/icons/X.png";
import singularStarGold from "../../assets/media/SingularStarGold.png";
import { formatHeroRingValue } from "../../utils/formatHeroRingValue";
import { usePnlAssetPreload } from "../../hooks/usePnlAssetPreload";
import { useMobilePinchZoom } from "../../hooks/useMobilePinchZoom";
import { usePnlShareImage } from "../../hooks/usePnlShareImage";
import { getNextPnlCalculationDate } from "../../utils/pnlCalculationDate";
import {
  formatCurrentPlacementDisplay,
  resolveTopPercent,
} from "../../utils/resolveTopPercent";

const normalizeUserPayload = (raw) => {
  if (!raw || typeof raw !== "object") return {};
  return {
    ...raw,
    transactionCount: raw.transactionCount ?? raw.transacationCount,
    sellingPlatformFee: raw.sellingPlatformFee ?? raw.sellingplatformFee,
    updatePnl: raw.updatePnl ?? raw.updatepnl,
    pnlTime: raw.pnlTime ?? null,
  };
};

const COUNT_DEFAULT_DURATION = 1.25;

/**
 * Dev-only: force ring PNL/ROI to negative values so red styling can be checked without
 * a losing wallet. Does not affect share image or tweet text (those use raw API fields).
 * Set to `true` for design QA; keep `false` for production.
 */
const FORCE_NEGATIVE_PNL_PREVIEW = false;

/**
 * Maps API PNL/ROI to the number shown in the hero ring (slides 2–3).
 * When preview is off, returns the API value (or 0 if invalid).
 * When preview is on: 0 → fallbackWhenZero; positive → negated; negative unchanged.
 */
const applyNegativePnlPreview = (value, fallbackWhenZero = -1) => {
  if (!FORCE_NEGATIVE_PNL_PREVIEW) {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
  }
  const n = Number(value);
  if (!Number.isFinite(n) || n === 0) return fallbackWhenZero;
  return n < 0 ? n : -Math.abs(n);
};

/** Total spent / volume are always shown as zero or positive. */
const toNonNegativeAmount = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, n);
};

const formatPodiumNftPnl = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0";
  if (n === 0 || parseFloat(n.toFixed(2)) === -0) return "0";
  const abs = Math.abs(n);
  if (abs < 1 && n !== 0) return n.toFixed(2);
  return n.toLocaleString(undefined, { maximumFractionDigits: 0 });
};

const formatPodiumRoiBadge = (roi) => {
  const n = Number(roi);
  if (!Number.isFinite(n)) return "0.0%";
  if (n > 0) return `+${n.toFixed(1)}%`;
  if (n < 0) return `${n.toFixed(1)}%`;
  return "0.0%";
};

const getPodiumRoiTone = (roi) => {
  const n = Number(roi);
  if (!Number.isFinite(n) || n === 0) return "white";
  return n > 0 ? "green" : "red";
};

/** Renders a span animated with [CountUp.js](https://inorganik.github.io/countUp.js/). */
const AnimatedCountUp = ({
  end,
  decimals = 2,
  prefix = "",
  suffix = "",
  duration = COUNT_DEFAULT_DURATION,
  className,
  /** Apply hero-ring display rules ({@link formatHeroRingValue}). */
  ringValueFormat = false,
  /** When `ringValueFormat` is true, allow `24.28` → `24:28` style (use for ROI / clock-like values only). */
  ringTimeAsClock = false,
  /** Thousand separators (e.g. dollar amounts). Ignored when `ringValueFormat` is true. */
  useGrouping = false,
}) => {
  const spanRef = useRef(null);
  const countUpRef = useRef(null);

  useEffect(() => {
    const el = spanRef.current;
    if (!el) return;

    const parsed = Number(end);
    const safeEnd = Number.isFinite(parsed) ? parsed : 0;

    const options = {
      decimalPlaces: decimals,
      duration,
      prefix: ringValueFormat ? "" : prefix,
      suffix: ringValueFormat ? "" : suffix,
      useGrouping,
      useEasing: true,
      ...(ringValueFormat
        ? {
          formattingFn: (num) =>
            prefix + formatHeroRingValue(num, { allowTimeFormat: ringTimeAsClock }) + suffix,
        }
        : {}),
    };

    const existing = countUpRef.current;
    if (existing && !existing.error) {
      existing.update(safeEnd);
      return undefined;
    }

    const instance = new CountUp(el, safeEnd, options);
    countUpRef.current = instance;

    if (!instance.error) {
      instance.start();
    }

    return () => {
      instance.onDestroy();
      countUpRef.current = null;
    };
  }, [end, decimals, prefix, suffix, duration, ringValueFormat, ringTimeAsClock, useGrouping]);

  return <span ref={spanRef} className={className} />;
};

const PLTrackerNoWallet = () => {
  const { isConnected } = useAccount();
  const [searchParams] = useSearchParams();
  const { userInfo, userRank } = useContext(UserContext);
  const {
    handleSearch,
    handleInputPaste,
    setSearchInput,
    currentWeekResult,
    lastWeekLeaderboard,
    leaderboardWeekTab,
    searchInput,
  } = useContext(LeaderboardContext);
  const isSmallDevice = useMediaQuery({ query: "(max-width: 768px)" });
  const searchBarRef = useRef(null);
  const searchBarRefMobile = useRef(null);
  const profileImageRef = useRef(null);
  const [imgErrors, setImgErrors] = useState({});
  const tabFromUrl = searchParams.get("tab");
  const activeTab = tabFromUrl === "leaderboard" ? "leaderboard" : "pnl";

  const [currentSlide, setCurrentSlide] = useState(0);
  const [isFading, setIsFading] = useState(false);
  const heroSliderRef = useRef(null);
  const wheelLockRef = useRef(false);
  const fadeTimeoutRef = useRef(null);
  const wheelTimeoutRef = useRef(null);
  const currentSlideRef = useRef(currentSlide);
  const isFadingRef = useRef(isFading);
  const touchStartYRef = useRef(null);
  const {
    containerRef: pinchZoomRef,
    reset: resetPinchZoom,
    isZoomed: isPinchZoomed,
  } = useMobilePinchZoom(isSmallDevice && activeTab === "pnl");

  const formatNum = (num, digits = 2) => {
    if (num === null || num === undefined || num === "") return "0";
    const parsed = Number(num);
    if (Number.isNaN(parsed)) return "0";
    return parsed.toFixed(digits).replace(/\.00$/, "");
  };

  const formatDate = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const liveUser = useMemo(
    () => normalizeUserPayload(userInfo || {}),
    [userInfo]
  );

  const isDataLoading = liveUser?.dataloading === true;

  // Raw API values (also used by share payload elsewhere).
  const netPnlEth = useMemo(() => {
    return Number(liveUser?.totalRevenue) || 0;
  }, [liveUser?.totalRevenue]);

  const roiPct = useMemo(() => {
    const n = Number(liveUser?.roi);
    return Number.isFinite(n) ? n : 0;
  }, [liveUser?.roi]);

  // Ring display values; may be overridden when FORCE_NEGATIVE_PNL_PREVIEW is true.
  const displayNetPnlEth = useMemo(
    () => applyNegativePnlPreview(netPnlEth, -1.25),
    [netPnlEth]
  );

  const displayRoiPct = useMemo(
    () => applyNegativePnlPreview(roiPct, -12),
    [roiPct]
  );

  const nextPnlCalculationDate = useMemo(
    () => getNextPnlCalculationDate(liveUser?.pnlTime),
    [liveUser?.pnlTime]
  );

  const profileDisplayLabel =
    liveUser?.twitter_username != null && String(liveUser.twitter_username).trim() !== ""
      ? `@${String(liveUser.twitter_username).replace(/^@+/, "")}`
      : liveUser?.walletAddress
        ? `${liveUser.walletAddress.slice(0, 6)}...${liveUser.walletAddress.slice(-4)}`
        : "@XPOORIA";

  const profileSrc = liveUser?.userAvatar;
  const showProfileSection = isConnected || liveUser?.isLogin;

  const { profileImageReady, setProfileImageReady } = usePnlAssetPreload({
    profileSrc,
    profileImageRef,
  });

  const handleProfileImageRef = useCallback(
    (node) => {
      profileImageRef.current = node;
      if (node?.complete && node.naturalWidth > 0) {
        setProfileImageReady(true);
      }
    },
    [setProfileImageReady]
  );

  const profileTraderStatus = useMemo(() => {
    const points = Number(userRank?.points);
    const topPct = resolveTopPercent(userRank, currentWeekResult);
    const pts = Number.isFinite(points) ? Math.max(0, Math.round(points)) : 0;
    return { topPct, pts };
  }, [userRank, currentWeekResult]);

  const leaderboardRankDisplay = useMemo(
    () => formatCurrentPlacementDisplay(userRank, currentWeekResult),
    [userRank, currentWeekResult]
  );

  const prizePointsDisplay = useMemo(() => {
    const points = Number(userRank?.points);
    const safe = Number.isFinite(points) ? Math.max(0, Math.round(points)) : 0;
    return safe.toLocaleString();
  }, [userRank?.points]);

  const slideData = useMemo(
    () => [
      {
        key: "paid",
        id: profileDisplayLabel,
        title: "You've Paid",
        type: "paid",
      },
      {
        key: "nftpnl",
        id: profileDisplayLabel,
        title: "YOUR NFT PNL",
        type: "nftpnl",
        alternatives: [
          { label: "Total spent", end: toNonNegativeAmount(liveUser?.totalspent), isRoi: false },
          { label: "ROI", end: displayRoiPct, isRoi: true },
          { label: "Total Volume", end: toNonNegativeAmount(liveUser?.totalVolume), isRoi: false },
        ],
        // description: "Based on your current account stats",
      },
      {
        key: "yourpnl",
        id: profileDisplayLabel,
        title: "NFT PNL",
        type: "yourpnl",
        statsCaption: "Peak ROI If You Bought These Instead",
        metrics: [
          {
            brandKey: "sandisk",
            brandIcon: sandiskIcon,
            brandAlt: "SanDisk",
            end: Number(userRank?.Sandisk) || 0,
            isRoi: false,
          },
          {
            brandKey: "pokemon",
            brandIcon: pokemonIcon,
            brandAlt: "Pokémon",
            end: Number(userRank?.Pokemon) || 0,
            isRoi: false,
            inlineLabel: "Cards:",
          },
          {
            brandKey: "gold",
            brandIcon: goldIcon,
            brandAlt: "Gold",
            end: Number(userRank?.Gold) || 0,
            isRoi: false,
            inlineLabel: "Gold:",
          },
        ],
      },
    ],
    [profileDisplayLabel, liveUser, displayRoiPct, userRank]
  );

  const currentContent = slideData[currentSlide];

  const goToSlide = useCallback((target) => {
    const safeTarget = Math.max(0, Math.min(target, slideData.length - 1));
    if (safeTarget === currentSlideRef.current || isFadingRef.current) return;

    isFadingRef.current = true;
    setIsFading(true);
    window.clearTimeout(fadeTimeoutRef.current);
    fadeTimeoutRef.current = window.setTimeout(() => {
      currentSlideRef.current = safeTarget;
      setCurrentSlide(safeTarget);
      isFadingRef.current = false;
      setIsFading(false);
    }, 220);
  }, [slideData.length]);

  useEffect(() => {
    return () => {
      window.clearTimeout(fadeTimeoutRef.current);
      window.clearTimeout(wheelTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    currentSlideRef.current = currentSlide;
  }, [currentSlide]);

  useEffect(() => {
    isFadingRef.current = isFading;
  }, [isFading]);

  useEffect(() => {
    if (activeTab === "leaderboard") {
      document.body.classList.add("leaderboard-bg");
    } else {
      document.body.classList.remove("leaderboard-bg");
    }
    return () => document.body.classList.remove("leaderboard-bg");
  }, [activeTab]);

  useEffect(() => {
    const root = document.documentElement;
    if (activeTab === "pnl") {
      document.body.classList.add("pnl-slider-locked");
      root.classList.add("pnl-slider-locked");
    } else {
      document.body.classList.remove("pnl-slider-locked");
      root.classList.remove("pnl-slider-locked");
    }
    return () => {
      document.body.classList.remove("pnl-slider-locked");
      root.classList.remove("pnl-slider-locked");
    };
  }, [activeTab]);

  useEffect(() => {
    const onMouseDown = (event) => {
      const t = event.target;
      const desktop = searchBarRef.current;
      const mobile = searchBarRefMobile.current;
      if (desktop?.contains(t) || mobile?.contains(t)) return;
      if (desktop) desktop.value = "";
      if (mobile) mobile.value = "";
      setSearchInput("");
    };
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [setSearchInput]);

  useEffect(() => {
    resetPinchZoom();
  }, [currentSlide, resetPinchZoom]);

  useEffect(() => {
    if (activeTab !== "pnl") return undefined;

    const slider = heroSliderRef.current;
    if (!slider) return undefined;

    const handleWheel = (event) => {
      event.preventDefault();
      event.stopPropagation();

      if (wheelLockRef.current || isFadingRef.current) return;
      if (Math.abs(event.deltaY) < 10) return;

      wheelLockRef.current = true;
      window.clearTimeout(wheelTimeoutRef.current);
      wheelTimeoutRef.current = window.setTimeout(() => {
        wheelLockRef.current = false;
      }, 420);

      const slide = currentSlideRef.current;
      if (event.deltaY > 0) {
        goToSlide(slide + 1);
      } else {
        goToSlide(slide - 1);
      }
    };

    const handleTouchStart = (event) => {
      if (isSmallDevice && (event.touches.length > 1 || isPinchZoomed())) return;
      touchStartYRef.current = event.touches[0]?.clientY ?? null;
    };

    const handleTouchMove = (event) => {
      if (isSmallDevice && (event.touches.length > 1 || isPinchZoomed())) return;
      if (touchStartYRef.current == null) return;
      event.preventDefault();
    };

    const handleTouchEnd = (event) => {
      if (isSmallDevice && isPinchZoomed()) {
        touchStartYRef.current = null;
        return;
      }
      const startY = touchStartYRef.current;
      touchStartYRef.current = null;
      if (startY == null || wheelLockRef.current || isFadingRef.current) return;

      const endY = event.changedTouches[0]?.clientY;
      if (endY == null) return;

      const deltaY = startY - endY;
      if (Math.abs(deltaY) < 36) return;

      wheelLockRef.current = true;
      window.clearTimeout(wheelTimeoutRef.current);
      wheelTimeoutRef.current = window.setTimeout(() => {
        wheelLockRef.current = false;
      }, 420);

      const slide = currentSlideRef.current;
      if (deltaY > 0) {
        goToSlide(slide + 1);
      } else {
        goToSlide(slide - 1);
      }
    };

    slider.addEventListener("wheel", handleWheel, { passive: false });
    slider.addEventListener("touchstart", handleTouchStart, { passive: true });
    slider.addEventListener("touchmove", handleTouchMove, { passive: false });
    slider.addEventListener("touchend", handleTouchEnd, { passive: true });

    return () => {
      slider.removeEventListener("wheel", handleWheel);
      slider.removeEventListener("touchstart", handleTouchStart);
      slider.removeEventListener("touchmove", handleTouchMove);
      slider.removeEventListener("touchend", handleTouchEnd);
    };
  }, [activeTab, goToSlide, isSmallDevice, isPinchZoomed]);

  const podiumUsers = useMemo(() => {
    const list = currentWeekResult?.user_list;
    if (!Array.isArray(list) || list.length < 3) return null;
    const sorted = [...list].sort((a, b) => (a.rank ?? 999) - (b.rank ?? 999));
    return sorted.slice(0, 5);
  }, [currentWeekResult]);

  const podiumOrder = useMemo(() => {
    if (!podiumUsers) return [];
    if (podiumUsers.length >= 5) {
      return [podiumUsers[4], podiumUsers[1], podiumUsers[0], podiumUsers[2], podiumUsers[3]];
    }
    return [podiumUsers[1], podiumUsers[0], podiumUsers[2]];
  }, [podiumUsers]);

  const leaderboardWeekResult = useMemo(
    () => (leaderboardWeekTab === "last" ? lastWeekLeaderboard : currentWeekResult),
    [leaderboardWeekTab, lastWeekLeaderboard, currentWeekResult],
  );

  const leaderboardWeektype = leaderboardWeekTab === "last" ? "Previous" : "current";

  const showLeaderboardPagination =
    !searchInput?.trim() &&
    Array.isArray(leaderboardWeekResult?.user_list) &&
    leaderboardWeekResult.user_list.length > 0;

  const leaderboardCurrentPage =
    Number(leaderboardWeekResult?.pagination?.currentPage) || 1;

  const showPodium =
    leaderboardCurrentPage === 1 &&
    !searchInput?.trim() &&
    podiumOrder.length >= 3;
  const { downloadImage, shareOnX, isSharing, isDownloading, isBusy, canShare, showShareBonus } = usePnlShareImage({
    liveUser,
    userRank,
    profileDisplayLabel,
    profileSrc,
    profileImageRef,
    isConnected,
    isDataLoading,
  });

  return (
    <div className="nowallet nowallet-slider-page">
      {/* ════════ YOUR PNL TAB ════════ */}
      {activeTab === "pnl" && (
        <div className="hero-slider" ref={heroSliderRef}>
          {/* <div className="hero-side-arcs" aria-hidden="true" /> */}
          <div className="hero-slider-dots" aria-label="Slides navigation">
            {slideData.map((slide, index) => (
              <button
                key={slide.key}
                type="button"
                className={`hero-dot ${currentSlide === index ? "active" : ""}`}
                onClick={() => goToSlide(index)}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>

          <div className="hero-static-stage">
            <div
              ref={pinchZoomRef}
              className={`hero-slide-content${isSmallDevice ? " hero-slide-content--pinch-zoom" : ""}`}
            >
              <div className="hero-circle">
                <div
                  className={`hero-profile-block${isDataLoading ? " hero-profile-block--during-load" : ""}`}
                >
                  {showProfileSection && profileSrc ? (
                    <img
                      ref={handleProfileImageRef}
                      src={profileSrc}
                      className={`hero-profile-image${
                        profileImageReady ? " hero-profile-image--loaded" : ""
                      }`}
                      alt="Profile"
                      referrerPolicy="no-referrer"
                      decoding="async"
                      fetchPriority="high"
                      onLoad={() => setProfileImageReady(true)}
                    />
                  ) : null}
                  {showProfileSection && profileDisplayLabel ? (
                    <p className="hero-profile-id">{profileDisplayLabel}</p>
                  ) : null}
                  {showProfileSection && !isDataLoading && currentContent.type === "yourpnl" ? (
                    <p className="hero-profile-status__text">
                      🏆  Top{" "}
                      <span className="hero-profile-status__accent">
                        {profileTraderStatus.topPct}%
                      </span>{" "}
                      NFT Trader{" "}
                      <span className="hero-profile-status__sep">|</span>{" "}
                      Receiving{" "}
                      <span className="hero-profile-status__accent">
                        {profileTraderStatus.pts}
                      </span>{" "}
                      Points{" "}
                      <img
                        src={singularStarGold}
                        className="hero-profile-status__sparkle"
                        alt=""
                        aria-hidden="true"
                      />
                    </p>
                  ) : null}
                </div>

                <div className="hero-circle-inner">
                  <img
                    src={pnlCharacters}
                    className={`hero-character-image${isDataLoading ? "" : " hero-character-image--loaded"}`}
                    alt="PNL Characters"
                    decoding="async"
                    fetchPriority="high"
                  />

                  <div className={`hero-loading-overlay${isDataLoading ? " active" : ""}`}>
                    <div className="hero-ring-loader" role="status" aria-label="Loading PNL League">
                      <span className="hero-ring-loader__track" aria-hidden="true" />
                      <span className="hero-ring-loader__arc" aria-hidden="true" />
                    </div>
                    <span className="hero-loader-text">LOADING YOUR STATS...</span>
               
                  </div>

                  <div
                    className={`hero-circle-overlay${isDataLoading ? " hero-circle-overlay--hidden" : ""}${currentContent.type === "yourpnl" ? " hero-circle-overlay--yourpnl" : ""
                      }`}
                  >
                    <div
                      className={`hero-content-panel${currentContent.type === "yourpnl" ? " hero-content-panel--yourpnl" : ""} ${isFading ? "fade-out" : "fade-in"
                        }`}
                    >
                      {currentContent.type === "paid" ? (
                        <>
                          <h2 className="hero-paid-title">{currentContent.title}</h2>
                          <div className="hero-metrics">
                            <div className="hero-metric-item">
                              <span className="hero-metric-label">Royalties</span>
                              <span className="hero-metric-value">
                                <AnimatedCountUp
                                  end={Number(liveUser?.sellingRoyaltyFee) || 0}
                                  decimals={2}
                                  duration={COUNT_DEFAULT_DURATION}
                                />
                                <img src={ethLogo} alt="ETH" />
                              </span>
                            </div>
                            <div className="hero-metric-divider" aria-hidden="true" />
                            <div className="hero-metric-item">
                              <span className="hero-metric-label">OpenSea Fees</span>
                              <span className="hero-metric-value">
                                <AnimatedCountUp
                                  end={Number(liveUser?.sellingPlatformFee) || 0}
                                  decimals={2}
                                  duration={COUNT_DEFAULT_DURATION}
                                />
                                <img src={ethLogo} alt="ETH" />
                              </span>
                            </div>
                          </div>
                          <p className="hero-description hero-description--txn-line">
                            <span className="hero-description__key">Across </span>
                            <span className="hero-description__value">
                              {formatNum(liveUser?.transactionCount, 0)}
                            </span>
                            <span className="hero-description__key"> NFT Txns</span>
                            <span className="hero-description__sep" aria-hidden="true">
                              {" "}
                              |{" "}
                            </span>
                            <span className="hero-description__key">Starting: </span>
                            <span className="hero-description__value">
                              {formatDate(
                                liveUser?.firstTransactionDate ||
                                liveUser?.firstTxnDate ||
                                liveUser?.startDate ||
                                liveUser?.createdAt,
                              ) || "—"}
                            </span>
                          </p>
                        </>
                      ) : null}

                      {currentContent.type === "nftpnl" ? (
                        <>
                          <div className="hero-nft-pnl-top-card">
                            <h2 className="hero-nft-title">{currentContent.title}</h2>
                            <div className={`hero-nft-main${displayNetPnlEth < 0 ? " hero-nft-main--negative" : ""}`}>
                              <span
                                className={`hero-nft-main-value${displayNetPnlEth < 0 ? " hero-nft-main-value--negative" : ""}`}
                              >
                                <AnimatedCountUp
                                  end={displayNetPnlEth}
                                  decimals={2}
                                  duration={COUNT_DEFAULT_DURATION}
                                  ringValueFormat
                                />
                              </span>
                              <img src={ethLogo} alt="ETH" />
                            </div>
                          </div>
                          <div className="hero-alt-row">
                            {currentContent.alternatives.map((item, index) => (
                              <React.Fragment key={item.label}>
                                {index > 0 ? <div className="hero-metric-divider" aria-hidden="true" /> : null}
                                <div className="hero-alt-item">
                                  <span className="hero-alt-label">{item.label}</span>
                                  <span
                                    className={`hero-alt-value${item.isRoi ? " hero-alt-value--roi" : ""}${item.isRoi && item.end < 0 ? " hero-alt-value--negative" : ""}`}
                                  >
                                    {item.isRoi ? (
                                      <AnimatedCountUp
                                        end={item.end}
                                        decimals={0}
                                        prefix={item.end > 0 ? "+" : ""}
                                        suffix="%"
                                        duration={COUNT_DEFAULT_DURATION}
                                      />
                                    ) : (
                                      <>
                                        <AnimatedCountUp
                                          end={item.end}
                                          decimals={2}
                                          duration={COUNT_DEFAULT_DURATION}
                                          ringValueFormat
                                        />
                                        <img src={ethLogo} alt="ETH" />
                                      </>
                                    )}
                                  </span>
                                </div>
                              </React.Fragment>
                            ))}
                          </div>
                        </>
                      ) : null}

                      {currentContent.type === "yourpnl" ? (
                        <>
                          <div className="hero-yourpnl-head-row">
                            <h2 className="hero-nft-title hero-nft-title--upper hero-nft-title--yourpnl-side">
                              <span className="hero-nft-title-stack-line">NFT</span>
                              <span className="hero-nft-title-stack-line">PNL</span>
                            </h2>
                            <div
                              className={`hero-nft-main hero-nft-main--yourpnl-inline${displayNetPnlEth < 0 ? " hero-nft-main--negative" : ""}`}
                            >
                              <span
                                className={`hero-nft-main-value${displayNetPnlEth < 0 ? " hero-nft-main-value--negative" : ""}`}
                              >
                                <AnimatedCountUp
                                  end={displayNetPnlEth}
                                  decimals={2}
                                  duration={COUNT_DEFAULT_DURATION}
                                  ringValueFormat
                                />
                              </span>
                              <img src={ethLogo} alt="ETH" />
                              <div
                                className={`hero-yourpnl-roi-chip${displayRoiPct < 0 ? " hero-yourpnl-roi-chip--negative" : ""}`}
                                role="group"
                                aria-label={`ROI ${Math.round(displayRoiPct)} percent`}
                              >

                                <span className="hero-yourpnl-roi-chip__pct">
                                  <AnimatedCountUp
                                    end={displayRoiPct}
                                    decimals={0}
                                    prefix={displayRoiPct > 0 ? "+" : ""}
                                    suffix="%"
                                    duration={COUNT_DEFAULT_DURATION}
                                  />
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="hero-yourpnl-stats-foot">
                            <div className="hero-stats-bar-shell">
                              <div className="hero-stats-grid">
                                {currentContent.metrics.map((item) => (
                                  <div key={item.brandKey} className="hero-stat-item">
                                    <div className="hero-stat-row">
                                      <div className="hero-stat-brand-line">
                                        <img
                                          src={item.brandIcon}
                                          alt={item.brandAlt}
                                          className={`hero-stat-brand-img hero-stat-brand-img--${item.brandKey}`}
                                          decoding="async"
                                        />
                                        {item.inlineLabel ? (
                                          <span className="hero-stat-inline-label">{item.inlineLabel}</span>
                                        ) : null}
                                      </div>
                                      <span
                                        className={`hero-stat-value ${item.isRoi ? "hero-stat-value--roi" : ""}`}
                                      >
                                        {item.isRoi ? (
                                          <AnimatedCountUp
                                            end={item.end}
                                            decimals={0}
                                            prefix={item.end > 0 ? "+" : ""}
                                            suffix="%"
                                            duration={COUNT_DEFAULT_DURATION}
                                          />
                                        ) : (
                                          <AnimatedCountUp
                                            key={`${item.brandKey}-${item.end}`}
                                            end={Math.round(Number(item.end)) || 0}
                                            decimals={0}
                                            prefix="$"
                                            useGrouping
                                            duration={COUNT_DEFAULT_DURATION}
                                          />
                                        )}
                                      </span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                            {currentContent.statsCaption ? (
                              <p className="hero-yourpnl-stats-caption">{currentContent.statsCaption}</p>
                            ) : null}
                          </div>
                        </>
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>
              <div
                className={`hero-scroll-hint${currentSlide === 0 ? "" : " hero-scroll-hint--hidden"}`}
                aria-hidden={currentSlide !== 0}
              >
                <div className="hero-scroll-hint__content">
                  <span className="chevron" />
                  <span className="scroll-label">Scroll For More</span>
                  <span className="chevron" />
                </div>
              </div>

            </div>
          </div>
        </div>
      )}

      {/* ════════ LEADERBOARD TAB ════════ */}
      {activeTab === "leaderboard" && (
        <div className="lb-leaderboard-shell">
          {showPodium && (
            <div className="scoreboard podium-scene">
              <div className="scoreboard__podiums podium-row">
                {podiumOrder.map((user, idx) => {
                  if (!user) return null;
                  const podiumRank = user.rank;
                  const rankClass =
                    podiumRank === 1 ? "first" : podiumRank === 2 ? "second" : podiumRank === 3 ? "third" : podiumRank === 4 ? "fourth" : "fifth";
                  const nftPnlEth = Number(user.totalRevenue) || 0;
                  const roiTone = getPodiumRoiTone(user.roi);

                  return (
                    <div
                      key={user.walletAddress || idx}
                      className={`scoreboard__podium is-visible rank-${podiumRank}${podiumRank === 1 ? " scoreboard__podium--first" : ""}${podiumRank === 2 ? " bump" : ""}`}
                    >
                      <div className="podium-floating-content">
                        <div className="podium-avatar-container">
                          {user.userAvatar && !imgErrors[user.walletAddress] ? (
                            <img
                              src={user.userAvatar}
                              alt={user.twitter_username || "User"}
                              className="podium-avatar"
                              onError={() => setImgErrors((p) => ({ ...p, [user.walletAddress]: true }))}
                            />
                          ) : (
                            <img src={profileImage} alt="Default" className="podium-avatar" />
                          )}
                        </div>
                        <div className="podium-username">
                          @{user.twitter_username || "Anonymous"}
                        </div>
                      </div>

                      <div
                        className={`scoreboard__podium-base scoreboard__podium-base--${rankClass} is-expanding podium-card`}
                      >
                        <div className="scoreboard__podium-rank rank-badge">{podiumRank}</div>
                        <div className="podium-stats-below">
                          <span className="podium-points-display">
                           NFT PNL
                          </span>
                          <span
                            className={`podium-nft-pnl-value`}
                          >
                            <span>{formatPodiumNftPnl(nftPnlEth)}</span>
                            <img
                              src={ethLogo}
                              alt=""
                              className="podium-stat-eth-icon"
                              aria-hidden="true"
                            />
                          </span>
                          <span className={`podium-roi-pill podium-roi-pill--${roiTone}`}>
                            {formatPodiumRoiBadge(user.roi)}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="lb-panel">
          <div className="leaderboard-chart-wrapper winner-leaderboard">
            <div className="leaderboard-chart-top-buttons">
              <p className="winner-leaderboard-title">LEADERBOARD</p>
              <div className="leaderboard-chart-top-right">
                <SearchIcon className="search-icon" />
                <input
                  ref={searchBarRef}
                  type="text"
                  className="leaderboard-search"
                  placeholder="SEARCH USER"
                  onChange={(e) => handleSearch(e)}
                  onPaste={(e) => handleInputPaste(e)}
                />
              </div>
            </div>

            <div className="leaderboard-sticky-top">
              <div className="leaderboard-your-stats" aria-label="Your stats">
                <div className="leaderboard-your-stats__title">YOUR STATS</div>
                <div className="leaderboard-your-stats__cards">
                  <div className="leaderboard-your-stats__item">
                    <div className="leaderboard-your-stats__label">CURRENT PLACEMENT</div>
                    <div className="leaderboard-your-stats__value leaderboard-your-stats__value--rank">
                      {leaderboardRankDisplay}
                    </div>
                  </div>
                  <div className="leaderboard-your-stats__item">
                    <div className="leaderboard-your-stats__label">PRIZE POINTS</div>
                    <div className="leaderboard-your-stats__value leaderboard-your-stats__value--prize">
                      <span>{prizePointsDisplay}</span>
                      <img
                        src={singularStarGold}
                        alt=""
                        className="leaderboard-your-stats__prize-star"
                        aria-hidden="true"
                      />
                    </div>
                  </div>
                </div>
                {nextPnlCalculationDate ? (
                  <div className="leaderboard-your-stats__next-calc">
                    <div className="leaderboard-your-stats__next-calc-label">YOUR STATS WILL UPDATE ON</div>
               
                    <div className="leaderboard-your-stats__next-calc-value">
                      {formatDate(nextPnlCalculationDate)}
                    </div>
                  </div>
                ) : null}
              </div>
              <div
                className={`leaderboard-top-table-headers ${!isSmallDevice ? "with-points" : "compact"
                  }`}
              >
                <div className="lb-col-rank">Rank</div>
                <div className="lb-col-user">User</div>
                {!isSmallDevice && (
                  <>
                    <div className="lb-col-volume">Total Volume</div>
                    <div className="lb-col-nft-pnl">NFT PNL</div>
                    <div className="lb-col-placement">Placement</div>
                    <div className="lb-col-prize">Prize Points</div>
                  </>
                )}
                {isSmallDevice && <div className="lb-col-nft-pnl">NFT PNL</div>}
                {/* Tier column (allowlist) — hidden for now
                {!isSmallDevice && <div className="lb-col-tier">Tier</div>}
                */}
              </div>
            </div>

            <div className="leaderboard-list">
              <Termresult />
            </div>
            {showLeaderboardPagination ? (
              <Pagination
                key={leaderboardWeektype}
                weektype={leaderboardWeektype}
                Paginationdata={leaderboardWeekResult?.pagination}
              />
            ) : null}
          </div>

          <div className="search-mobile lb-leaderboard-search-mobile">
            <SearchIcon className="search-icon" />
            <input
              ref={searchBarRefMobile}
              type="text"
              className="leaderboard-search"
              placeholder="SEARCH USER"
              onChange={(e) => handleSearch(e)}
              onPaste={(e) => handleInputPaste(e)}
            />
          </div>
          </div>
        </div>
      )}

      {activeTab === "pnl" && (
        <div
          className={`pnl-share-actions${currentSlide === 2 ? " pnl-share-actions--visible" : ""}${isBusy ? " pnl-share-actions--busy" : ""}`}
          role="group"
          aria-label="Share your PNL card"
          aria-hidden={currentSlide !== 2}
        >
          <button
            type="button"
            className="pnl-share-actions__btn pnl-share-actions__btn--share"
            onClick={shareOnX}
            disabled={!canShare || isBusy}
            aria-busy={isSharing}
          >
            <span className="pnl-share-actions__label">
              {isSharing ? "Generating…" : "Share On"}
            </span>
            <img src={xIcon} alt="" className="pnl-share-actions__x" draggable={false} />
            {showShareBonus ? (
              <span className="pnl-share-actions__bonus">
                (1000
                <img src={singularStarGold} alt="" className="pnl-share-actions__star" aria-hidden="true" draggable={false} />
                Bonus)
              </span>
            ) : null}
          </button>
          <button
            type="button"
            className="pnl-share-actions__btn"
            onClick={downloadImage}
            disabled={!canShare || isBusy}
            aria-busy={isDownloading}
          >
            {isDownloading ? "Generating…" : "Download Image"}
          </button>
        </div>
      )}
    </div>
  );
};

export default PLTrackerNoWallet;


